import { JSONSchema7 } from "json-schema"
import { MetricRequest } from "../Client/MetricRequest"
import { Id } from "../Utility/Id"
import { TemplateMetadata } from "../DataProgram/TemplateMetadata"
import { LabelerConfig } from "../DataProgram/LabelerRestriction"
import type { UiSchema } from "@rjsf/core"
import { SchemaForm } from "universal-schema/lib"
import {
    jobInputModernizeOptions,
    jobOutputModernizeOptions,
    marketplaceJobInputModernizeOptions,
    marketplaceJobOutputModernizeOptions,
    modernizeSchemaIfNeeded
} from "../Client/Schema"

export type TemplateName = Id<"TemplateName"> & string

export function TemplateName(value: string): TemplateName {
    return value as TemplateName
}

export type TemplateSlug = Id<"TemplateSlug"> & string

export function TemplateSlug(value: string): TemplateSlug {
    return value as TemplateSlug
}

export function nameToSlug(name: TemplateName): TemplateSlug {
    return TemplateSlug(name.replace(/_/g, "-").replace(/\.router/g, ""))
}

export enum Protocol {
    WEBSOCKET = "WEBSOCKET",
    QUMES = "QUMES"
}

export type Visibility = "GROUP" | "PUBLIC"

export interface APIAppTemplateSchemaModel {
    inputSchema: JSONSchema7
    inputUiSchema?: UiSchema
    outputSchema: JSONSchema7
    outputUiSchema?: UiSchema
}

export interface APIAppTemplateModel extends APIAppTemplateSchemaModel {
    name: TemplateName
    appParamsSchema: null | { params: JSONSchema7 | null }
    defaultAppParams: null | { params: unknown }
    appParamsUiSchema?: null | { params: UiSchema }
    defaultAppMetrics: {
        metric: null | {} | MetricRequest
    }
    defaultConstraints: LabelerConfig
    metadata: TemplateMetadata
    uuid: string
    created: string
    measurement_job_cost: number
    protocol: Protocol
    visibility: Visibility
    trainingWorkflow?: string
}

export function getInputSchema(
    template: APIAppTemplateModel,
    marketplaceMode: boolean
): SchemaForm {
    return modernizeSchemaIfNeeded(
        template.inputSchema,
        template.inputUiSchema,
        template.metadata.parameter_description?.inputs,
        marketplaceMode
            ? marketplaceJobInputModernizeOptions
            : jobInputModernizeOptions
    )
}

export function getOutputSchema(
    template: APIAppTemplateModel,
    marketplaceMode: boolean
): SchemaForm {
    return modernizeSchemaIfNeeded(
        template.outputSchema,
        template.outputUiSchema,
        template.metadata.parameter_description?.outputs,
        marketplaceMode
            ? marketplaceJobOutputModernizeOptions
            : jobOutputModernizeOptions
    )
}

export function getParameterSchema(
    template: APIAppTemplateModel | MarketplaceTemplateModel,
    exampleAsDefault: boolean = false
): SchemaForm | undefined {
    const params = template.appParamsSchema?.params ?? undefined

    return params === undefined
        ? undefined
        : modernizeSchemaIfNeeded(
              params,
              template.appParamsUiSchema?.params,
              template.metadata.parameter_description?.parameters,
              {
                  enableCopyTextButton: true,
                  enableMarkdown: true,
                  exampleAsDefault
              }
          )
}

export interface APISampleAppModel {
    uuid: string
    appParams: { params: JSONSchema7 }
    dataInputs: unknown[]
}

export type MarketplaceTemplateModel = Pick<
    APIAppTemplateModel,
    | "name"
    | "appParamsSchema"
    | "defaultAppParams"
    | "inputSchema"
    | "outputSchema"
    | "metadata"
    | "inputUiSchema"
    | "outputUiSchema"
    | "appParamsUiSchema"
>
